import { useState } from 'react';
import { useClub } from '../app/club';
import { useRepo } from '../app/services';
import { useClubMutation } from '../data/hooks';
import type { Building, Door, DoorKind, Location, Lock } from '../lib/types';
import { MODEL_HINT, MODEL_LABEL } from '../lib/format';
import { doorOptionsForLock, lockOptionsForDoor } from '../lib/doors';
import { canAny } from '../lib/roles';
import { errorText } from '../lib/errors';
import { addressFromLocation, cleanAddress, validateAddress, type AddressErrors } from '../lib/address';
import { AddressFields } from './AddressFields';
import { Button, Dialog, Field, useToast } from './ui';

function FormError({ error }: { error?: string | null }) {
  if (!error) return null;
  return (
    <div className="alert alert-danger" role="alert">
      {error}
    </div>
  );
}

/** Locatie toevoegen of bewerken (RPC save_location): naam plus gewone adresvelden. Verwijderen kan alleen zonder deuren. */
export function LocationDialog({ location, hasDoors, onClose }: { location: Location | null; hasDoors: boolean; onClose: () => void }) {
  const { orgId, roles, readOnly } = useClub();
  const repo = useRepo();
  const toast = useToast();
  const [name, setName] = useState(location?.name ?? '');
  const [address, setAddress] = useState(addressFromLocation(location));
  const [errors, setErrors] = useState<AddressErrors & { name?: string; form?: string }>({});
  const save = useClubMutation(orgId, () => repo.saveLocation(orgId, { id: location?.id ?? null, name, ...cleanAddress(address) }));
  const remove = useClubMutation(orgId, () => repo.deleteLocation(location!.id));
  const legacy = location && !location.street && location.address ? location.address : null;
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={location ? `${location.name} bewerken` : 'Locatie toevoegen'}
      description="Een locatie is een sportpark of terrein met een eigen adres. Daaronder zet je de gebouwen, zoals de kantine of een ballenhok."
    >
      <form
        className="stack"
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          const errs: typeof errors = validateAddress(address, { required: true });
          if (name.trim().length < 2) errs.name = 'Vul de naam van de locatie in.';
          setErrors(errs);
          if (Object.keys(errs).length) return;
          try {
            await save.mutateAsync(undefined);
            toast(location ? 'Locatie opgeslagen' : `Locatie ${name.trim()} toegevoegd`);
            onClose();
          } catch (err) {
            setErrors({ form: errorText(err, 'Opslaan mislukt') });
          }
        }}
      >
        <Field label="Naam locatie" hint="Bijvoorbeeld: Sportpark De Sniep" error={errors.name}>
          {(p) => <input {...p} className="input" value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <AddressFields value={address} onChange={setAddress} errors={errors} required />
        {legacy && <p className="muted small">Eerder ingevuld adres: {legacy}</p>}
        <FormError error={errors.form} />
        <div className="dialog-actions">
          {location && canAny('door.archive', roles) && !readOnly && (
            <Button
              variant="ghost"
              disabled={hasDoors || remove.isPending}
              title={hasDoors ? 'Er staan nog deuren op deze locatie.' : undefined}
              style={{ marginRight: 'auto' }}
              onClick={async () => {
                try {
                  await remove.mutateAsync(undefined);
                  toast('Locatie verwijderd');
                  onClose();
                } catch (err) {
                  setErrors({ form: errorText(err, 'Verwijderen mislukt') });
                }
              }}
            >
              Locatie verwijderen
            </Button>
          )}
          <Button onClick={onClose}>Annuleren</Button>
          <Button variant="primary" type="submit" disabled={save.isPending}>
            Opslaan
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Gebouw toevoegen of bewerken (RPC save_building); archiveren kan alleen zonder actieve deuren (archive_building). */
export function BuildingDialog({
  building,
  locations,
  defaultLocationId,
  activeDoors,
  onClose,
}: {
  building: Building | null;
  locations: Location[];
  defaultLocationId?: string;
  /** Aantal niet-gearchiveerde deuren in dit gebouw. */
  activeDoors: number;
  onClose: () => void;
}) {
  const { orgId } = useClub();
  const repo = useRepo();
  const toast = useToast();
  const [form, setForm] = useState({
    name: building?.name ?? '',
    notes: building?.notes ?? '',
    location_id: building?.location_id ?? defaultLocationId ?? (locations.length === 1 ? locations[0]!.id : ''),
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useClubMutation(orgId, () =>
    repo.saveBuilding(orgId, { id: building?.id ?? null, location_id: form.location_id, name: form.name, notes: form.notes.trim() || null }),
  );
  const archive = useClubMutation(orgId, () => repo.archiveBuilding(building!.id));
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={building ? `${building.name} bewerken` : 'Gebouw toevoegen'}
      description="Een gebouw op de locatie, zoals de kantine, de kleedkamers of een ballenhok. De deuren en ruimtes vallen eronder."
    >
      <form
        className="stack"
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          const errs: Record<string, string> = {};
          if (form.name.trim().length < 2) errs.name = 'Vul de naam van het gebouw in.';
          if (!form.location_id) errs.location = 'Kies een locatie.';
          setErrors(errs);
          if (Object.keys(errs).length) return;
          try {
            await save.mutateAsync(undefined);
            toast(building ? 'Gebouw opgeslagen' : `Gebouw ${form.name.trim()} toegevoegd`);
            onClose();
          } catch (err) {
            setErrors({ form: errorText(err, 'Opslaan mislukt') });
          }
        }}
      >
        <Field label="Naam gebouw" hint="Bijvoorbeeld: Kantine, Kleedkamers of Ballenhok 1" error={errors.name}>
          {(p) => <input {...p} className="input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />}
        </Field>
        {(locations.length > 1 || !form.location_id) && (
          <Field label="Locatie" error={errors.location}>
            {(p) => (
              <select {...p} className="input" value={form.location_id} onChange={(e) => setForm((f) => ({ ...f, location_id: e.target.value }))}>
                <option value="">Kies een locatie</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}
        <Field label="Notitie (optioneel)" hint="Bijvoorbeeld waar het gebouw staat: naast veld 2.">
          {(p) => <input {...p} className="input" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />}
        </Field>
        <FormError error={errors.form} />
        <div className="dialog-actions">
          {building && (
            <Button
              variant="ghost"
              disabled={activeDoors > 0 || archive.isPending}
              title={activeDoors > 0 ? 'Er zitten nog deuren in dit gebouw.' : undefined}
              style={{ marginRight: 'auto' }}
              onClick={async () => {
                try {
                  await archive.mutateAsync(undefined);
                  toast(`${building.name} gearchiveerd`);
                  onClose();
                } catch (err) {
                  setErrors({ form: errorText(err, 'Archiveren mislukt') });
                }
              }}
            >
              Gebouw archiveren
            </Button>
          )}
          <Button onClick={onClose}>Annuleren</Button>
          <Button variant="primary" type="submit" disabled={save.isPending}>
            {building ? 'Opslaan' : 'Gebouw toevoegen'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * Deur/ruimte toevoegen of bewerken (RPC save_door). Alleen de deur zelf: welk slot erop komt, koppel je later
 * apart ("Slot koppelen" of "Sloten zonder deur").
 */
export function DoorDialog({
  door,
  locations,
  buildings,
  defaultBuildingId,
  onClose,
}: {
  door: Door | null;
  locations: Location[];
  /** Gebouwen van de club (gearchiveerde worden alleen getoond als de deur er nog in zit). */
  buildings: Building[];
  defaultBuildingId?: string;
  onClose: () => void;
}) {
  const { orgId, roles, readOnly } = useClub();
  const repo = useRepo();
  const toast = useToast();
  const safety = canAny('door.safety', roles) && !readOnly;
  const choosable = buildings.filter((b) => !b.archived_at || b.id === door?.building_id);
  const [form, setForm] = useState({
    name: door?.name ?? '',
    building_id: door?.building_id ?? defaultBuildingId ?? (choosable.length === 1 ? choosable[0]!.id : ''),
    kind: (door?.kind ?? '') as DoorKind | '',
    escape_route: door?.escape_route ?? false,
    passage_mode_allowed: door?.passage_mode_allowed ?? false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useClubMutation(orgId, () =>
    repo.saveDoor(orgId, {
      id: door?.id ?? null,
      building_id: form.building_id,
      name: form.name,
      kind: form.kind as DoorKind,
      escape_route: safety ? form.escape_route : null,
      passage_mode_allowed: safety ? form.passage_mode_allowed : null,
    }),
  );
  const byLocation = locations
    .map((l) => ({ location: l, buildings: choosable.filter((b) => b.location_id === l.id) }))
    .filter((g) => g.buildings.length > 0);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={door ? `${door.name} bewerken` : 'Deur toevoegen'}
      description="Een deur hoort bij een ruimte in een gebouw. Het slot koppel je daarna apart aan de deur."
    >
      <form
        className="stack"
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          const errs: Record<string, string> = {};
          if (form.name.trim().length < 2) errs.name = 'Vul de naam van de ruimte in.';
          if (!form.building_id) errs.building = 'Kies een gebouw.';
          if (!form.kind) errs.kind = 'Kies buitendeur of binnendeur.';
          setErrors(errs);
          if (Object.keys(errs).length) return;
          try {
            await save.mutateAsync(undefined);
            toast(door ? 'Deur opgeslagen' : `Deur ${form.name.trim()} toegevoegd`);
            onClose();
          } catch (err) {
            setErrors({ form: errorText(err, 'Opslaan mislukt') });
          }
        }}
      >
        <Field label="Gebouw" error={errors.building} hint={byLocation.length === 0 ? 'Voeg eerst een gebouw toe, bijvoorbeeld de kantine.' : undefined}>
          {(p) => (
            <select {...p} className="input" value={form.building_id} onChange={(e) => setForm((f) => ({ ...f, building_id: e.target.value }))}>
              <option value="">Kies een gebouw</option>
              {byLocation.map((g) =>
                byLocation.length === 1 ? (
                  g.buildings.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))
                ) : (
                  <optgroup key={g.location.id} label={g.location.name}>
                    {g.buildings.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </optgroup>
                ),
              )}
            </select>
          )}
        </Field>
        <Field label="Naam ruimte" hint="Bijvoorbeeld: Kleedkamer 1 of Materiaalhok" error={errors.name}>
          {(p) => <input {...p} className="input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />}
        </Field>
        <fieldset className="stack" style={{ border: 0, padding: 0, margin: 0, gap: 8 }}>
          <legend className="label" style={{ marginBottom: 8 }}>
            Soort deur
          </legend>
          <div className="choice-group">
            <label className="choice">
              <input type="radio" name="door-kind" checked={form.kind === 'exterior'} onChange={() => setForm((f) => ({ ...f, kind: 'exterior' }))} />
              <span>
                <strong>Buitendeur</strong>
                <span>Zit in weer en wind.</span>
              </span>
            </label>
            <label className="choice">
              <input type="radio" name="door-kind" checked={form.kind === 'interior'} onChange={() => setForm((f) => ({ ...f, kind: 'interior' }))} />
              <span>
                <strong>Binnendeur</strong>
                <span>Bijvoorbeeld een kleedkamer of kantoor.</span>
              </span>
            </label>
          </div>
          {errors.kind && (
            <span className="error" role="alert">
              {errors.kind}
            </span>
          )}
        </fieldset>
        {safety && (
          <div className="stack" style={{ gap: 12 }}>
            <div>
              <label className="row" style={{ gap: 8 }}>
                <input
                  type="checkbox"
                  checked={form.escape_route}
                  aria-describedby="help-escape"
                  onChange={(e) => setForm((f) => ({ ...f, escape_route: e.target.checked }))}
                />
                Deze deur is een nooduitgang
              </label>
              <span className="check-help" id="help-escape">
                Bij een nooduitgang moet je altijd van binnen naar buiten kunnen. De installateur controleert dit bij het opleveren; passagemodus is
                hier niet nodig.
              </span>
            </div>
            <div>
              <label className="row" style={{ gap: 8 }}>
                <input
                  type="checkbox"
                  checked={form.passage_mode_allowed}
                  aria-describedby="help-passage"
                  onChange={(e) => setForm((f) => ({ ...f, passage_mode_allowed: e.target.checked }))}
                />
                Passagemodus toestaan
              </label>
              <span className="check-help" id="help-passage">
                Alleen deuren met dit vinkje kunnen een tijd openstaan, bijvoorbeeld de kantine op wedstrijddagen.
              </span>
            </div>
          </div>
        )}
        <FormError error={errors.form} />
        <div className="dialog-actions">
          <Button onClick={onClose}>Annuleren</Button>
          <Button variant="primary" type="submit" disabled={save.isPending}>
            {door ? 'Opslaan' : 'Deur toevoegen'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function lockLabel(l: Lock) {
  return `${l.serial ?? 'zonder serienummer'} · ${MODEL_LABEL[l.model]} (${MODEL_HINT[l.model]})`;
}

/** Kies een slot van de club (nog op geen deur) voor deze deur (RPC link_lock_to_door). */
export function LinkLockDialog({ door, locks, doors, onClose }: { door: Door; locks: Lock[]; doors: Door[]; onClose: () => void }) {
  const { orgId } = useClub();
  const repo = useRepo();
  const toast = useToast();
  const options = lockOptionsForDoor(door, locks, doors);
  const [lockId, setLockId] = useState(options.find((o) => !o.blocked)?.lock.id ?? '');
  const [error, setError] = useState<string | null>(null);
  const link = useClubMutation(orgId, (id: string) => repo.linkLockToDoor(door.id, id));
  const chosen = options.find((o) => o.lock.id === lockId);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Slot koppelen aan ${door.name}`}
      description="Kies het slot dat op deze deur komt. Check het serienummer op de doos of de achterkant van het slot."
    >
      {options.length === 0 ? (
        <p className="muted">Er zijn geen losse sloten meer. Antreja wijst nieuwe sloten toe aan de club.</p>
      ) : (
        <div className="stack">
          {options.length > 0 && (
            <Field label="Slot" hint={chosen?.blocked ?? (door.expected_model ? `Deze deur verwacht een ${MODEL_LABEL[door.expected_model]}.` : undefined)}>
              {(p) => (
                <select {...p} className="input" value={lockId} onChange={(e) => setLockId(e.target.value)}>
                  <option value="">Kies een slot</option>
                  {options.map((o) => (
                    <option key={o.lock.id} value={o.lock.id} disabled={!!o.blocked}>
                      {lockLabel(o.lock)}
                      {o.blocked ? ' (ander model)' : ''}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          )}
          <p className="muted small">Na het koppelen plaatst de installateur het slot en verbindt het met de Antreja-app.</p>
        </div>
      )}
      <FormError error={error} />
      <div className="dialog-actions">
        <Button onClick={onClose}>Annuleren</Button>
        <Button
          variant="primary"
          disabled={!lockId || !!chosen?.blocked || link.isPending}
          onClick={async () => {
            try {
              await link.mutateAsync(lockId);
              toast(`Slot ${chosen?.lock.serial ?? ''} gekoppeld aan ${door.name}`);
              onClose();
            } catch (err) {
              setError(errorText(err, 'Koppelen mislukt'));
            }
          }}
        >
          Koppelen
        </Button>
      </div>
    </Dialog>
  );
}

/** Vanuit "Sloten zonder deur": kies de deur voor dit slot. */
export function LinkDoorDialog({
  lock,
  doors,
  locations,
  buildings = [],
  onClose,
}: {
  lock: Lock;
  doors: Door[];
  locations: Location[];
  buildings?: Building[];
  onClose: () => void;
}) {
  const { orgId } = useClub();
  const repo = useRepo();
  const toast = useToast();
  const options = doorOptionsForLock(lock, doors);
  const [doorId, setDoorId] = useState(options.length === 1 ? options[0]!.id : '');
  const [error, setError] = useState<string | null>(null);
  const link = useClubMutation(orgId, (id: string) => repo.linkLockToDoor(id, lock.id));
  const locName = new Map(locations.map((l) => [l.id, l.name]));
  const bldName = new Map(buildings.map((b) => [b.id, b.name]));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Slot ${lock.serial ?? ''} koppelen`} description={`${MODEL_LABEL[lock.model]}: ${MODEL_HINT[lock.model]}. Kies de deur waar dit slot op komt.`}>
      {options.length === 0 ? (
        <p className="muted">Er is geen lege deur waar een {MODEL_LABEL[lock.model]} op past. Voeg eerst een deur toe.</p>
      ) : (
        <Field label="Deur">
          {(p) => (
            <select {...p} className="input" value={doorId} onChange={(e) => setDoorId(e.target.value)}>
              <option value="">Kies een deur</option>
              {options.map((d) => (
                <option key={d.id} value={d.id}>
                  {[d.building_id ? bldName.get(d.building_id) : undefined, d.name].filter(Boolean).join(' · ')} ({locName.get(d.location_id) ?? ''})
                </option>
              ))}
            </select>
          )}
        </Field>
      )}
      <FormError error={error} />
      <div className="dialog-actions">
        <Button onClick={onClose}>Annuleren</Button>
        <Button
          variant="primary"
          disabled={!doorId || link.isPending}
          onClick={async () => {
            try {
              await link.mutateAsync(doorId);
              toast(`Slot gekoppeld aan ${options.find((d) => d.id === doorId)?.name ?? 'de deur'}`);
              onClose();
            } catch (err) {
              setError(errorText(err, 'Koppelen mislukt'));
            }
          }}
        >
          Koppelen
        </Button>
      </div>
    </Dialog>
  );
}
